import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { openChannelByEpisodeChatChannelsByEpisodeEpisodeIdPost } from '../../services/api';
import type { ChannelHeader } from '../../services/api';
import { useStore } from '../../store';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { chatKeys } from './useChatQueries';

const chartPatientId = (pathname: string) =>
  pathname.match(/^\/patients\/([^/]+)/)?.[1];

/**
 * The launcher press (and Cmd/Ctrl+.). Opens the inbox at once. From a patient
 * chart with an episode selected it then asks the backend for that episode's
 * care-team conversation, creating it for a care-team member (EMR-2470), and
 * switches to it. The inbox stays for any other answer, so a failure is never
 * worse than the press was before.
 */
export function useOpenChatFromChart(): () => void {
  const queryClient = useQueryClient();
  const openChat = useStore(state => state.openChat);
  const openChatChannel = useStore(state => state.openChatChannel);

  // The route is read when the answer arrives, not when the button is pressed:
  // `chatContextEpisodeId` outlives the chart, so a late answer for an earlier
  // chart must not open its conversation.
  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  return useCallback(() => {
    openChat();
    const episodeId = useStore.getState().chatContextEpisodeId;
    if (!episodeId || !chartPatientId(pathnameRef.current)) return;

    openChannelByEpisodeChatChannelsByEpisodeEpisodeIdPost({
      path: { episode_id: episodeId },
      throwOnError: true,
    })
      .then(({ data, status }) => {
        const header = data as ChannelHeader;
        queryClient.setQueryData(chatKeys.careTeam(episodeId), header);
        if (status === 201) {
          void queryClient.invalidateQueries({ queryKey: chatKeys.inbox() });
        }
        const { isChatOpen, chatView, chatContextEpisodeId } =
          useStore.getState();
        const currentPatient = chartPatientId(pathnameRef.current);
        if (
          isChatOpen &&
          chatView === 'inbox' &&
          chatContextEpisodeId === episodeId &&
          header.patient_id === currentPatient
        ) {
          openChatChannel(header.id);
        }
      })
      .catch((error: unknown) => {
        const status = getHttpStatus(error);
        if (status === 404 || status === 403) return;
        console.error('Failed to open the care-team conversation', error);
      });
  }, [openChat, openChatChannel, queryClient]);
}
